import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import AssetCard from "@/components/AssetCard";
import UploadAssetModal from "@/components/UploadAssetModal";
import BulkUploadModal from "@/components/BulkUploadModal";
import FolderTile, { createFolderPrompt } from "@/components/FolderTile";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { Plus, Layers, FolderPlus, ChevronRight, Home, Download, CheckSquare, X } from "lucide-react";
import { toast } from "sonner";
import { downloadAssetsZip } from "@/lib/download";

export default function CategoryPage({ category, title, eyebrow, description }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null); // {id, name, breadcrumb}
  const [sub, setSub] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sort, setSort] = useState("recent"); // recent | az | za
  const [rootDragOver, setRootDragOver] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const canManage = user && (user.role === "admin");
  // Viewers can't download artwork — hide bulk/download UI on that specific page.
  const canBulkDownload = !(user && user.role === "viewer" && category === "artwork");

  // Exit select mode + clear selection whenever the visible list changes
  // (category swap, folder navigation, sub-filter). This keeps state honest.
  const catRef = useRef(category);
  if (catRef.current !== category) {
    catRef.current = category;
    setCurrentFolder(null);
    setItems([]);
    setFolders([]);
    setSub("all");
    setSelectMode(false);
    setSelected(new Set());
  }

  const load = useCallback(() => {
    const assetParams = { category, limit: 300 };
    if (currentFolder?.id) assetParams.folder_id = currentFolder.id;
    else assetParams.root = true;
    const folderParams = { category };
    if (currentFolder?.id) folderParams.parent_id = currentFolder.id;
    return Promise.all([
      api.get("/assets", { params: assetParams }),
      api.get("/folders", { params: folderParams }),
    ]).then(([a, f]) => {
      // Guard: only apply results if the category hasn't changed while the request was in flight.
      if (catRef.current !== category) return;
      setItems(a.data);
      setFolders(f.data);
    });
  }, [category, currentFolder]);

  useEffect(() => { load(); }, [load]);
  // Live-sync: refetch on tab focus + every 30s so partners see admin edits without hard refresh.
  // Pause when the user is mid-selection to avoid clobbering their checked state.
  useAutoRefresh(load, { enabled: !selectMode });

  function handleChanged(evt) {
    if (evt?.deletedId) setItems((prev) => prev.filter((a) => a.id !== evt.deletedId));
    else if (evt?.updated) setItems((prev) => prev.map((a) => (a.id === evt.updated.id ? evt.updated : a)));
    else load();
  }

  async function openFolder(fid) {
    try {
      const { data } = await api.get(`/folders/${fid}`);
      setCurrentFolder(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function goToBreadcrumb(fid) {
    if (!fid) { setCurrentFolder(null); return; }
    openFolder(fid);
  }

  async function handleRename(fid, newName) {
    try {
      await api.patch(`/admin/folders/${fid}`, { name: newName });
      toast.success("Renamed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function handleDeleteFolder(fid) {
    try {
      await api.delete(`/admin/folders/${fid}`);
      toast.success("Folder deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function handleDropAsset(assetId, folderId) {
    try {
      await api.post(`/admin/assets/${assetId}/move`, { folder_id: folderId });
      toast.success(folderId ? "Moved to folder" : "Moved to root");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function newFolder() {
    const created = await createFolderPrompt({
      category,
      parentId: currentFolder?.id || null,
    });
    if (created) load();
  }

  const subs = ["all", ...new Set(items.map((a) => a.subcategory).filter(Boolean))];
  const filtered = useMemo(() => {
    const base = sub === "all" ? items : items.filter((a) => a.subcategory === sub);
    if (sort === "az" || sort === "za") {
      const sorted = [...base].sort((a, b) =>
        (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base", numeric: true })
      );
      return sort === "za" ? sorted.reverse() : sorted;
    }
    return base;
  }, [items, sub, sort]);

  const breadcrumb = currentFolder?.breadcrumb || [];

  return (
    <div className="space-y-8 slide-up" data-testid={`page-${category}`}>
      <BackButton />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">{eyebrow}</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{description}</p>
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto [&>button]:flex-1 sm:[&>button]:flex-none [&>button]:justify-center">
          {canBulkDownload && (
            <button
              onClick={() => {
                setSelectMode((s) => !s);
                setSelected(new Set());
              }}
              className={`border px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 transition ${
                selectMode
                  ? "border-[#FBC90C] bg-[#FBC90C] text-black"
                  : "border-border bg-surface hover:bg-surface-elev"
              }`}
              data-testid="category-select-btn"
            >
              <CheckSquare className="h-3.5 w-3.5" /> {selectMode ? "Selecting" : "Select"}
            </button>
          )}
          {canBulkDownload && !selectMode && filtered.length > 0 && (
            <button
              onClick={() =>
                downloadAssetsZip({
                  ids: filtered.map((a) => a.id),
                  name: currentFolder?.name || category,
                })
              }
              className="border border-border bg-surface px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 hover:bg-surface-elev transition"
              data-testid="category-download-all-btn"
              title="Download every asset shown as one zip"
            >
              <Download className="h-3.5 w-3.5" /> Download All
            </button>
          )}
          {canManage && !selectMode && (
            <>
              <button
                onClick={newFolder}
                className="border border-border bg-surface px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 hover:bg-surface-elev transition"
                data-testid="new-folder-btn"
              >
                <FolderPlus className="h-3.5 w-3.5" /> New Folder
              </button>
              <button
                onClick={() => setBulkOpen(true)}
                className="border border-border bg-surface px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 hover:bg-surface-elev transition"
                data-testid="category-bulk-btn"
              >
                <Layers className="h-3.5 w-3.5" /> Bulk Upload
              </button>
              <button
                onClick={() => setUploadOpen(true)}
                className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2"
                data-testid="category-upload-btn"
              >
                <Plus className="h-3.5 w-3.5" /> Upload Asset
              </button>
            </>
          )}
        </div>
      </header>

      {selectMode && <SelectionBar
        selected={selected}
        setSelected={setSelected}
        setSelectMode={setSelectMode}
        filtered={filtered}
        currentFolder={currentFolder}
        category={category}
      />}

      {/* Breadcrumb — visible when inside a folder */}
      {(currentFolder || rootDragOver) && (
        <div
          className={`flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest border rounded-sm px-3 py-2 transition ${
            rootDragOver ? "border-foreground bg-surface-elev" : "border-border bg-surface"
          }`}
          data-testid="breadcrumb"
          onDragOver={(e) => { if (currentFolder) { e.preventDefault(); setRootDragOver(true); } }}
          onDragLeave={() => setRootDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setRootDragOver(false);
            const id = e.dataTransfer.getData("application/x-apollo-asset-id");
            if (id) handleDropAsset(id, null); // drop on root
          }}
        >
          <button
            type="button"
            onClick={() => goToBreadcrumb(null)}
            className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
            data-testid="crumb-root"
          >
            <Home className="h-3 w-3" /> {title}
          </button>
          {breadcrumb.map((b, i) => (
            <span key={b.id} className="flex items-center gap-1.5">
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              {i === breadcrumb.length - 1 ? (
                <span className="text-foreground" data-testid={`crumb-${b.id}`}>{b.name}</span>
              ) : (
                <button type="button" onClick={() => goToBreadcrumb(b.id)} className="hover:text-foreground text-muted-foreground" data-testid={`crumb-${b.id}`}>
                  {b.name}
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {subs.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {subs.map((s) => (
            <button
              key={s}
              onClick={() => setSub(s)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.2em] border rounded-sm transition capitalize ${
                sub === s ? "border-foreground bg-foreground text-background" : "border-border hover:border-border-strong"
              }`}
              data-testid={`sub-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-b border-border pb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          {folders.length > 0 && <span>{folders.length} {folders.length === 1 ? "folder" : "folders"} · </span>}
          {filtered.length} {filtered.length === 1 ? "asset" : "assets"}
        </span>
        <div className="flex items-center gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Sort</span>
          {[
            { k: "recent", label: "Recent" },
            { k: "az", label: "A–Z" },
            { k: "za", label: "Z–A" },
          ].map((opt) => (
            <button
              key={opt.k}
              onClick={() => setSort(opt.k)}
              className={`px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] rounded-sm transition ${
                sort === opt.k
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`sort-${opt.k}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {folders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="folder-grid">
          {folders.map((f) => (
            <FolderTile
              key={f.id}
              folder={f}
              canManage={canManage}
              onOpen={openFolder}
              onRename={handleRename}
              onDelete={handleDeleteFolder}
              onDropAsset={handleDropAsset}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map((a) => (
          <AssetCard
            key={a.id}
            asset={a}
            onChanged={handleChanged}
            selectMode={selectMode}
            selected={selected.has(a.id)}
            onToggleSelect={(id) => {
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
              });
            }}
          />
        ))}
        {filtered.length === 0 && folders.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground py-16 text-center border border-dashed border-border rounded-sm">
            Nothing here yet.{canManage && " Upload the first asset above, or create a folder to organize."}
          </div>
        )}
      </div>

      <UploadAssetModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultCategory={category}
        defaultFolderId={currentFolder?.id || null}
        onUploaded={() => load()}
      />
      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        defaultCategory={category}
        defaultFolderId={currentFolder?.id || null}
        onUploaded={() => load()}
      />
    </div>
  );
}

/**
 * Selection bar — sticks to the top on desktop, becomes a bottom sheet on phones.
 * We track viewport width in state (not classes) because Tailwind can't switch
 * between `position: sticky` and `position: fixed` cleanly at a single breakpoint
 * without one overriding the other.
 */
function SelectionBar({ selected, setSelected, setSelectMode, filtered, currentFolder, category }) {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const mobileStyle = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 40,
    paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
    borderColor: "#FBC90C",
  };
  const desktopStyle = {
    position: "fixed",
    top: 76,
    left: 264, // 240px sidebar + 24px main padding
    right: 32,
    zIndex: 40,
    borderColor: "#FBC90C",
  };

  const bar = (
    <div
      className={`z-30 flex items-center justify-between px-4 py-3 bg-surface ${
        isMobile
          ? "border-t rounded-none shadow-[0_-4px_20px_rgba(0,0,0,0.35)]"
          : "border rounded-sm shadow-lg"
      }`}
      style={isMobile ? mobileStyle : desktopStyle}
      data-testid="category-selection-bar"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground truncate">
          {selected.size} / {filtered.length}
        </span>
        <button
          onClick={() => {
            if (selected.size === filtered.length) setSelected(new Set());
            else setSelected(new Set(filtered.map((a) => a.id)));
          }}
          className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground transition"
          data-testid="category-select-all"
        >
          {selected.size === filtered.length ? "Clear" : "All"}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={async () => {
            if (selected.size === 0) return toast.error("Select at least one asset");
            const ok = await downloadAssetsZip({
              ids: Array.from(selected),
              name: `${currentFolder?.name || category}-selection`,
            });
            if (ok) {
              setSelected(new Set());
              setSelectMode(false);
            }
          }}
          disabled={selected.size === 0}
          className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2 disabled:opacity-40"
          data-testid="category-download-selected-btn"
        >
          <Download className="h-3.5 w-3.5" /> Download {selected.size > 0 ? selected.size : ""}
        </button>
        <button
          onClick={() => { setSelectMode(false); setSelected(new Set()); }}
          className="p-2 rounded-sm hover:bg-surface-elev"
          data-testid="category-selection-cancel"
          aria-label="Exit selection"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // Portal to document.body — the page container has a `slide-up` transform
  // that would otherwise clip fixed positioning to itself.
  return createPortal(bar, document.body);
}


